/**
 * Pure helpers over the viewer-state v2 payload.
 *
 * Server statuses are authoritative — these helpers only reshape data
 * (flattening steps for the sequential learner list, resume resolution,
 * checklist rehydration, celebration diffing). No locking is computed here.
 */

import type { ChecklistTaskView, PathItem } from "../types";
import type { ViewerState } from "../types/viewerState";

/**
 * Flattens steps into the ordered learner item list.
 * Steps arrive in topological order (edges first; sort_key, id tiebreak) and items in
 * (position, id) order within each step — the payload order is the render order.
 *
 * The owning step's time-release (unlockAfterDays/unlockAt) is denormalized
 * onto each item so the (item-centric) content area can tell a time-locked item
 * apart from a prerequisite-locked one without re-grouping by step.
 */
export const flattenStepItems = (state: ViewerState | null): PathItem[] => {
  if (!state) return [];
  return state.steps.flatMap((step, stepIndex) =>
    step.items.map((item) => ({
      ...item,
      unlockAfterDays: step.unlockAfterDays ?? null,
      unlockAt: step.unlockAt ?? null,
      memoryGate: step.memoryGate ?? null,
      stepId: step.id,
      stepLabel: step.label,
      stepPartLabel: step.partLabel ?? null,
      stepIndex,
    })),
  );
};

/**
 * Resolves the initially selected item: the server resume pointer when it is
 * visible, otherwise the first available item, otherwise the first item.
 */
export const resolveInitialItemId = (
  state: ViewerState | null,
): number | null => {
  const items = flattenStepItems(state);
  if (items.length === 0) return null;

  const currentItemId = state?.enrollment.currentItemId ?? null;
  if (
    currentItemId !== null &&
    items.some((item) => item.id === currentItemId)
  ) {
    return currentItemId;
  }

  const firstAvailable = items.find((item) => item.status === "available");
  if (firstAvailable) return firstAvailable.id;

  return items[0].id;
};

/**
 * Rehydrates the checklist view from the server's per-checkbox progress.
 * Falls back to the item's configured checklist (all unchecked) only when
 * no progress payload exists (e.g. admin preview).
 */
export const buildChecklistView = (item: PathItem): ChecklistTaskView => {
  const configItems = item.checklistItems ?? [];
  const progressItems = item.checklistProgress?.items;

  const items = progressItems
    ? progressItems.map((entry) => ({
        index: entry.index,
        name: entry.name,
        description: configItems[entry.index]?.description,
        isCompleted: entry.completed,
        completedAt: entry.completedAt ?? undefined,
        completedBy: entry.completedByName ?? undefined,
        isOverdue: false,
      }))
    : configItems.map((configItem, index) => ({
        index,
        name: configItem.name,
        description: configItem.description,
        isCompleted: false,
        isOverdue: false,
      }));

  return {
    taskId: item.id,
    title: item.taskName ?? "Checklist Task",
    description: item.taskDescription ?? "",
    items,
    requireAll: true,
  };
};

export interface CelebrationDiff {
  newlyCompletedMilestoneItemIds: number[];
  pathJustCompleted: boolean;
}

/**
 * Diffs two viewer states to detect celebration triggers after a completion
 * refetch: milestone items that flipped to completed (incl. server-side
 * milestone auto-complete fixpoint) and the pathComplete flip.
 */
export const diffCelebrations = (
  previous: ViewerState | null,
  next: ViewerState,
): CelebrationDiff => {
  const previouslyCompleted = new Set(
    flattenStepItems(previous)
      .filter((item) => item.status === "completed")
      .map((item) => item.id),
  );

  const newlyCompletedMilestoneItemIds = flattenStepItems(next)
    .filter(
      (item) =>
        item.type === "milestone" &&
        item.status === "completed" &&
        !previouslyCompleted.has(item.id),
    )
    .map((item) => item.id);

  const pathJustCompleted =
    next.progress.pathComplete && !(previous?.progress.pathComplete ?? false);

  return { newlyCompletedMilestoneItemIds, pathJustCompleted };
};
